import { createContext, useReducer, useEffect, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { GlobalContext } from './global'
import { GlobalStoreContext } from './globalStore'
import {
  getLastTimeRange,
  downloadCsv,
  numFixed,
  rateTitleFormat,
  tabMetricFormat,
  toPercent,
  transformUTC0ToLocalTime,
} from 'src/utils'
import { getUserActivityApi, getRevenueDeuApi } from 'src/api/cohorts'

type Props = {
  data: any
  holdData: any
  revenueData: any
  loading: boolean
  tabLoading: boolean
  revenueLoading: boolean
  gameSelect: any[]
  country: any[]
  timeShow: string
  type: string
  platform: any[]
  time: [string, string]
  placementOption: any[]
  placement: any[]
  filterDialogOpen: boolean
  gameListObject: any
  tabType: string
  chartList: Array<any>
  checkedChartLine: Array<string>
  resetIndex: number
  newChartType: string
}

type Action = {
  type: string
  payload?: any
}

type Reducer = (prevState: Props, action: Action) => Props
const getTimeRange = (range: [string, string] | number): [string, string] => {
  // custom date range
  return typeof range === 'number' ? getLastTimeRange(range) : range
}

const initialState: Props = {
  loading: false,
  revenueLoading: false,
  tabLoading: false,
  data: {},
  holdData: {},
  revenueData: {},
  gameSelect: [],
  country: [],
  timeShow: 'last_week',
  type: 'overall',
  platform: [],
  time: getTimeRange(7),
  placementOption: [],
  placement: [],
  filterDialogOpen: false,
  gameListObject: {},
  tabType: 'rate',
  chartList: [
    {
      label: 'All',
      value: 'All',
      color: '#FF8E00',
    },
  ],
  checkedChartLine: [],
  resetIndex: 0,
  newChartType: 'Graph',
}

const colorList = ['#FF8E00', '#90cc75', '#f07474', '#5a70b5']
const reducer: Reducer = (prevState: Props, action: Action): Props => {
  switch (action.type) {
    case 'data':
      let allData = action.payload.data
      let chartList: any = []
      let checkedChartLine: any = []
      const keys = allData ? Object.keys(allData) : []
      if (!keys.length) {
        allData = []
        chartList = [
          {
            label: 'All',
            value: 'All',
            color: '#FF8E00',
          },
        ]
        checkedChartLine = ['All']
      }
      const labels = action.payload.days
      const datasets: any = []
      keys.forEach((item: any, i: number) => {
        const color: string =
          colorList[i] ||
          '#' + Math.random().toString(16).substr(2, 6).toUpperCase()
        const label =
          prevState.gameListObject[item]?.name ||
          (item === 'all' ? 'All' : item)
        datasets.push({
          label,
          data: allData[item][prevState.tabType],
          pointRadius: 2.5,
          borderColor: color,
          backgroundColor: color,
          tension: 0.2,
          hitRadius: 10,
        })
        chartList.push({
          label,
          value: label,
          color,
          total: allData[item][prevState.tabType].reduce(
            (a: number, b: number) => a + b
          ),
        })
        checkedChartLine.push(label)
      })
      const data = {
        labels,
        datasets,
      }
      return {
        ...prevState,
        data,
        chartList: chartList.sort((a: any, b: any) => b.total - a.total),
        checkedChartLine,
      }
    case 'time':
      if (action.payload === 'Last Week') {
        action.payload = getTimeRange(7)
      } else if (action.payload === 'Last Month') {
        action.payload = getTimeRange(30)
      } else if (action.payload === 'Last 3 Month') {
        action.payload = getTimeRange(90)
      } else {
        action.payload = getTimeRange(action.payload)
      }
      return { ...prevState, time: action.payload }

    case 'holdData':
      const holdData = JSON.parse(JSON.stringify(action.payload))
      const disabled = action.payload?.tabDisabled || false
      const untilTime = action.payload?.untilTime || null
      if (holdData.relative_change) {
        let trend = 'up'
        if (holdData.relative_change.engagement_rate >= 0) {
          trend = 'up'
        } else {
          trend = 'down'
        }
        holdData.relative_change.engagement_rate = {
          value: toPercent(Math.abs(holdData.relative_change.engagement_rate)),
          trend,
          disabled,
          untilTime,
        }
        if (holdData.relative_change.deu >= 0) {
          trend = 'up'
        } else {
          trend = 'down'
        }
        holdData.relative_change.deu = {
          value: tabMetricFormat(Math.abs(holdData.relative_change.deu), 'dav'),
          trend,
          disabled,
          untilTime,
        }
        if (holdData.relative_change.imp_deu >= 0) {
          trend = 'up'
        } else {
          trend = 'down'
        }
        holdData.relative_change.imp_deu = {
          value: numFixed(Math.abs(holdData.relative_change.imp_deu), 4),
          trend,
          disabled,
          untilTime,
        }
      }
      return { ...prevState, holdData }
    case 'revenueData':
      const revenueData = JSON.parse(JSON.stringify(action.payload))
      const tabDisabled = action.payload?.tabDisabled || false
      const tabUntilTime = action.payload?.untilTime || null
      if (revenueData.relative_change) {
        let trend = 'up'
        if (revenueData.relative_change.revenue_deu >= 0) {
          trend = 'up'
        } else {
          trend = 'down'
        }
        revenueData.relative_change.revenue_deu = {
          value:
            '$' +
            numFixed(Math.abs(revenueData.relative_change.revenue_deu), 4),
          trend,
          disabled: tabDisabled,
          untilTime: tabUntilTime,
        }
      } else {
        revenueData.relative_change = {
          revenue_deu: {
            value: '$0',
            trend: 'up',
            disabled: tabDisabled,
            untilTime: tabUntilTime,
          },
        }
      }
      return { ...prevState, revenueData }
    case 'loading':
    case 'revenueLoading':
    case 'tabLoading':
    case 'gameListObject':
    case 'gameSelect':
    case 'country':
    case 'timeShow':
    case 'type':
    case 'platform':
    case 'placementOption':
    case 'placement':
    case 'filterDialogOpen':
    case 'tabType':
    case 'checkedChartLine':
    case 'chartList':
    case 'resetIndex':
    case 'newChartType':
      return { ...prevState, [action.type]: action.payload }
    default:
      throw new Error(`Unhandled action type: ${action.type}`)
  }
}

type Context = {
  state: Props
  dispatch: (value: Action) => void
  getUnitEngagement: () => void
  getRevenueDeu: () => void
  searchData: () => void
  changeTabType: (tabType: string) => void
  exportData: () => void
}

export const EngagementContext = createContext<Context>({
  state: initialState,
  dispatch: (value: Action) => {},
  getUnitEngagement: () => {},
  getRevenueDeu: () => {},
  searchData: () => {},
  changeTabType: (tabType: string) => {},
  exportData: () => {},
})

export const EngagementContextProvider = ({
  children,
}: JSX.ElementChildrenAttribute): JSX.Element => {
  const [state, dispatch] = useReducer<Reducer>(reducer, initialState)
  const { state: GlobalState, errorCatch } = useContext(GlobalContext)
  const { state: globalStoreState, dispatch: globalStoreDispatch } =
    useContext(GlobalStoreContext)
  const params: any = useParams()
  // handlers
  const searchData = () => {
    getUnitEngagement()
    getRevenueDeu()
  }
  const getUnitEngagement = () => {
    dispatch({ type: 'loading', payload: true })
    dispatch({ type: 'tabLoading', payload: true })
    if (globalStoreState.fromWelcome) {
      dispatch({ type: 'time', payload: globalStoreState.time })
      dispatch({ type: 'timeShow', payload: 'custom' })
      dispatch({ type: 'gameSelect', payload: globalStoreState.gameSelect })
    }
    const filterData = {
      from_date: globalStoreState.fromWelcome
        ? globalStoreState.time[0]
        : state.time[0],
      to_date: globalStoreState.fromWelcome
        ? globalStoreState.time[1]
        : state.time[1],
      app_keys: globalStoreState.fromWelcome
        ? globalStoreState.gameSelect.map(item => item.app_key)
        : state.gameSelect.map(item => item.app_key),
      platform: state.platform.map(item => item.value),
      countries: state.country.map(item => item.code),
      ad_types: state.type === 'overall' ? [] : [state.type],
    }
    globalStoreDispatch({
      type: 'engagementData',
      payload: {
        filter: {
          time: globalStoreState.fromWelcome
            ? globalStoreState.time
            : state.time,
          timeShow: state.timeShow,
          gameSelect: globalStoreState.fromWelcome
            ? globalStoreState.gameSelect
            : state.gameSelect,
          platform: state.platform,
          country: state.country,
          type: state.type,
        },
      },
    })
    getUserActivityApi(filterData)
      .then(({ data }: any) => {
        if (params.page !== 'revenue_deu') {
          dispatch({ type: 'tabType', payload: params.page })
          dispatch({ type: 'data', payload: data })
          globalStoreDispatch({
            type: 'engagementData',
            payload: {
              tabType: params.page,
              data: data,
            },
          })
        }
        dispatch({ type: 'holdData', payload: data })
        globalStoreDispatch({
          type: 'engagementData',
          payload: {
            holdData: data,
          },
        })
      })
      .catch((err: any) => {
        if (
          err?.response?.status === 503 &&
          err?.response?.data?.code === 'trino_migration'
        ) {
          let untilTime = null
          if (err?.response?.data?.extra?.after) {
            untilTime = transformUTC0ToLocalTime(
              err?.response?.data?.extra?.after,
              'Do MMM YYYY hh:mm a'
            )
          }
          dispatch({
            type: 'holdData',
            payload: { tabDisabled: true, untilTime },
          })
          globalStoreDispatch({
            type: 'engagementData',
            payload: {
              holdData: { tabDisabled: true, untilTime },
            },
          })
        } else {
          errorCatch(err)
          globalStoreDispatch({
            type: 'engagementData',
            payload: {
              holdData: {},
            },
          })
        }
        if (params.page !== 'revenue_deu') {
          globalStoreDispatch({
            type: 'engagementData',
            payload: {
              tabType: params.page,
              data: {},
            },
          })
        }
      })
      .finally(() => {
        dispatch({ type: 'loading', payload: false })
        dispatch({ type: 'tabLoading', payload: false })
      })
  }
  const getRevenueDeu = () => {
    dispatch({ type: 'revenueLoading', payload: true })
    dispatch({ type: 'loading', payload: true })
    if (globalStoreState.fromWelcome) {
      dispatch({ type: 'time', payload: globalStoreState.time })
      dispatch({ type: 'timeShow', payload: 'custom' })
      dispatch({ type: 'gameSelect', payload: globalStoreState.gameSelect })
    }
    const data = {
      from_date: globalStoreState.fromWelcome
        ? globalStoreState.time[0]
        : state.time[0],
      to_date: globalStoreState.fromWelcome
        ? globalStoreState.time[1]
        : state.time[1],
      app_keys: globalStoreState.fromWelcome
        ? globalStoreState.gameSelect.map(item => item.app_key)
        : state.gameSelect.map(item => item.app_key),
    }
    getRevenueDeuApi(data)
      .then(({ data }: any) => {
        if (params.page === 'revenue_deu') {
          dispatch({ type: 'tabType', payload: params.page })
          dispatch({ type: 'data', payload: data })
          globalStoreDispatch({
            type: 'engagementData',
            payload: {
              tabType: params.page,
              data: data,
            },
          })
        }
        dispatch({ type: 'revenueData', payload: data })
        globalStoreDispatch({
          type: 'engagementData',
          payload: {
            revenueData: data,
          },
        })
      })
      .catch((err: any) => {
        if (
          err?.response?.status === 503 &&
          err?.response?.data?.code === 'trino_migration'
        ) {
          let untilTime = null
          if (err?.response?.data?.extra?.after) {
            untilTime = transformUTC0ToLocalTime(
              err?.response?.data?.extra?.after,
              'Do MMM YYYY hh:mm a'
            )
          }
          dispatch({
            type: 'revenueData',
            payload: { tabDisabled: true, untilTime },
          })
          globalStoreDispatch({
            type: 'engagementData',
            payload: {
              revenueData: { tabDisabled: true, untilTime },
            },
          })
        } else {
          errorCatch(err)
          globalStoreDispatch({
            type: 'engagementData',
            payload: {
              revenueData: {},
            },
          })
        }
        if (params.page === 'revenue_deu') {
          globalStoreDispatch({
            type: 'engagementData',
            payload: {
              tabType: params.page,
              data: {},
            },
          })
        }
      })
      .finally(() => {
        dispatch({ type: 'revenueLoading', payload: false })
        dispatch({ type: 'loading', payload: false })
      })
  }
  const changeTabType = (tabType: string) => {
    dispatch({ type: 'tabType', payload: tabType })
    dispatch({
      type: 'data',
      payload: tabType === 'revenue_deu' ? state.revenueData : state.holdData,
    })
  }
  const typeList = {
    overall: 'Overall',
    banner: 'Banner',
    interstitial: 'Interstitial',
    reward_video: 'Rewarded Video',
    nativeads: 'Native Ads',
    openads: 'App Open',
    rewardinter: 'Rewarded Interstitial',
    android: 'Android',
    all: 'All',
  }
  const tabList = {
    rate: 'Ad Engagement Rate',
    deu: 'DAV',
    imp_deu: rateTitleFormat('Impressions', 'DAV'),
    revenue_deu: rateTitleFormat('Revenue', 'DAV'),
  }
  const exportData = () => {
    const arr = Object.keys(state.holdData.data)
    const days = state.holdData.days
    const name = `MAS_${days[0]}_${
      days[days.length - 1]
    }_ad_unit_engagement.csv`
    let csv = ''
    // 拼接筛选条件
    csv +=
      'TAB:,' +
      tabList[state.tabType] +
      ',,TYPE:,' +
      typeList[state.type] +
      ',,COUNTRY:,' +
      (state.country.map(item => item.name).join(',') || 'all') +
      ',,PLATFORM:,' +
      (state.platform.map(item => item.label).join(',') || 'all') +
      ',,APPLICATION:,' +
      (state.gameSelect.map(item => item.name).join(',') || 'all') +
      '\n' +
      '\n'
    // 循环拼接数据
    csv +=
      ',,' +
      arr.map(item => state.gameListObject[item]?.name || item).join(',') +
      '\n'
    days.forEach((item: any, index: number) => {
      csv += ',' + item
      arr.forEach((element: any, i: number) => {
        csv += ',' + state.holdData.data[element][state.tabType][index]
      })
      csv += '\n'
    })
    downloadCsv(csv, name, 'text/csv;encoding:utf-8')
  }
  useEffect(() => {
    dispatch({ type: 'gameListObject', payload: GlobalState.gameListObject })
  }, [GlobalState.gameListObject])
  useEffect(() => {
    if (globalStoreState.engagementData.filter) {
      Object.keys(globalStoreState.engagementData.filter).forEach(
        (item: string) => {
          dispatch({
            type: item,
            payload: globalStoreState.engagementData.filter[item],
          })
        }
      )
      Object.keys(globalStoreState.engagementData)
        .filter((item: string) => item !== 'filter')
        .forEach((item: string) => {
          dispatch({
            type: item,
            payload: globalStoreState.engagementData[item],
          })
        })
      return
    }
    getUnitEngagement()
    getRevenueDeu()
    // eslint-disable-next-line
  }, [])
  useEffect(() => {
    if (state.resetIndex) {
      searchData()
    }
    // eslint-disable-next-line
  }, [state.resetIndex])
  // returns
  return (
    <EngagementContext.Provider
      value={{
        state,
        dispatch,
        getUnitEngagement,
        getRevenueDeu,
        changeTabType,
        exportData,
        searchData,
      }}
    >
      {children}
    </EngagementContext.Provider>
  )
}
